import './utils/log';

import 'babel-polyfill';

import { bootstrap } from './bootstrap';

import { ApolloClient, ApolloProvider, createHttpLink, InMemoryCache } from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from 'apollo-link-error';
import { ApolloLink } from 'apollo-link';
import { get } from 'lodash';
import { v5 as uuidv5 } from 'uuid';
import { QueryClient, QueryClientProvider } from '@zg-rentals/react-query5';

import ThemeProvider from '@zg-rentals/multibrand-ui/lib/ThemeProvider';
import { ThemeProvider as SCThemeProvider } from 'styled-components';
import { CometTheme } from '@zillow/zrs-comet';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, matchPath, Route, Switch } from 'react-router-dom';
import { Provider } from 'react-redux';

import logger from './utils/log';
import App from '../shared/pages/App';
import configureStore from '../shared/configureStore';

import { checkApiAuthorization } from '@zg-rentals/shields-js';
import { BASE } from '../shared/constants/paths';
import { getApplicationsTabPath } from '../shared/constants/urls';
import { listingApplicationsTabLoadingStatusUpdate } from '../shared/actions/loadingStatusActionCreators';
import { ToastProvider } from '@zillow/constellation';
import generatePossibleTypes from './utils/generatePossibleTypes';
import { rentalPrefixUrlSelector, zgGraphUriSelector } from '../shared/selectors';
import { ZgGraphContext } from '../shared/contexts/ZgGraphContext';
import { logError } from '@zg-rentals/log-error';
import { RpErrorBoundary } from '@zg-rentals/rp-bootstrap-browser';
import { getGlobalMonitor } from '@zg-rentals/monitor-browser';
import { getClient } from '@zg-rentals/ts-client-http-client';
import { setClient as setIdVerificationClient } from '@zg-rentals/ts-id-verification-client';

bootstrap.register();

const monitor = getGlobalMonitor();
global.monitor = monitor;

const renderApp = () => {
  const initialState = window.__INITIAL_STATE__;
  const store = configureStore(initialState);
  const state = store.getState();
  const { app, user, listing } = state;
  const { zgEnv } = app;

  setIdVerificationClient(getClient().extend({ prefixUrl: rentalPrefixUrlSelector(state) }));
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        refetchOnWindowFocus: false,
      },
    },
  });

  const errorLink = onError(({ graphQLErrors }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach((error) => {
        if (error.message === 'downstream dependency failure' && error.path && error.path.length > 0) {
          const httpStatus = error?.extensions?.httpStatus;
          const path = error?.path[0];
          if (httpStatus) {
            monitor.count({ name: 'raApiCall ' + httpStatus });
          }
          if (httpStatus && path) {
            monitor.count({ name: 'raApiCall ' + path + ' ' + httpStatus });
          }
          logError({ error });
        }
      });
    }
  });

  const zgGraphClient = new ApolloClient({
    defaultOptions: {
      query: {
        ssr: false,
      },
      watchQuery: {
        ssr: false,
      },
    },
    cache: new InMemoryCache({
      possibleTypes: generatePossibleTypes(true),
    }),
    link: ApolloLink.from([
      errorLink,
      createHttpLink({
        credentials: 'include',
        headers: {
          'x-caller-id': 'ra-landlord-web-ssr',
        },
        uri: zgGraphUriSelector(state),
      }),
    ]),
  });

  const client = new ApolloClient({
    defaultOptions: {
      query: {
        ssr: false,
      },
      watchQuery: {
        ssr: false,
      },
    },
    link: ApolloLink.from([
      onError(({ graphQLErrors, networkError }) => {
        if (graphQLErrors) {
          const unauthenticatedError = graphQLErrors.find(
            (error) => get(error, 'extensions.code') === 'UNAUTHENTICATED',
          );
          const brownoutError = graphQLErrors.find((error) => get(error, 'extensions.code') === 'BROWNOUT');
          if (unauthenticatedError) {
            checkApiAuthorization({ res: unauthenticatedError.extensions.exception.response, brand: 'zillow' });
          }
          if (brownoutError) {
            if (!matchPath(window.location.pathname, { path: BASE, exact: true })) {
              window.location = getApplicationsTabPath(store.getState().listing.propertyId);
            }
            store.dispatch(
              listingApplicationsTabLoadingStatusUpdate({
                message: brownoutError.message,
                showSpinner: false,
                show: true,
                showCustomerSupportMessage: true,
              }),
            );
          }
        }
        if (networkError) {
          logger.warn(`[Network error]: ${networkError}`);
        }
      }),
      new BatchHttpLink({
        uri: '/rental-manager/applications/graphql',
        credentials: 'include',
        headers: {
          'x-csrf-token': state.app.csrfToken,
          'x-user-ra-guid': uuidv5(state.user.userToken, uuidv5.DNS),
        },
      }),
    ]),
    cache: new InMemoryCache({ possibleTypes: generatePossibleTypes(false) }).restore(window.__INITIAL_APOLLO_STATE__),
  });

  const root = createRoot(document.getElementById('app-root'));
  root.render(
    <RpErrorBoundary>
      <ZgGraphContext.Provider value={{ client: zgGraphClient }}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider brand="zillow" noStyleSheet>
            <SCThemeProvider theme={CometTheme}>
              <Provider store={store}>
                <ApolloProvider client={client}>
                  <ToastProvider placement="top" duration={5000}>
                    <BrowserRouter>
                      <Switch>
                        <Route path="/rental-manager/applications">
                          <App host={window.location.host} pathname={window.location.pathname} />
                        </Route>
                      </Switch>
                    </BrowserRouter>
                  </ToastProvider>
                </ApolloProvider>
              </Provider>
            </SCThemeProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </ZgGraphContext.Provider>
    </RpErrorBoundary>,
  );
};

renderApp();
